import {
  defaultOperators,
  type Field,
  type Operator,
} from 'react-querybuilder';
import type { RQBFieldOut } from '@/api/generated/schemas';

/**
 * Async fields — value is loaded from a Refine resource via autocomplete.
 * `optionValue`/`optionLabel` are the resource fields used for value/label.
 */
export const ASYNC_FIELD_RESOURCE_MAP: Record<
  string,
  { resource: string; optionValue: string; optionLabel: string }
> = {
  id: {
    resource: 'user_catalog.publications',
    optionValue: 'id',
    optionLabel: 'title',
  },
  publisher_id: {
    resource: 'user_catalog.publishers',
    optionValue: 'id',
    optionLabel: 'name',
  },
  main_series_id: {
    resource: 'user_catalog.series',
    optionValue: 'id',
    optionLabel: 'name',
  },
  author_id: {
    resource: 'user_catalog.authors',
    optionValue: 'id',
    optionLabel: 'name',
  },
  lector_id: {
    resource: 'user_catalog.lectors',
    optionValue: 'id',
    optionLabel: 'name',
  },
  emotion_id: {
    resource: 'user_catalog.emotions',
    optionValue: 'id',
    optionLabel: 'name',
  },
  tag_id: {
    resource: 'user_catalog.tags',
    optionValue: 'id',
    optionLabel: 'name',
  },
  category_id: {
    resource: 'user_catalog.categories',
    optionValue: 'id',
    optionLabel: 'name',
  },
  collection_id: {
    resource: 'user_catalog.managed_collections',
    optionValue: 'id',
    optionLabel: 'name',
  },
  thema_codes: {
    resource: 'content_catalog.thema_codes',
    optionValue: 'code',
    optionLabel: 'code_with_description',
  },
};

/** Fields rendered with a date picker. */
export const DATE_FIELDS = new Set(['created_at', 'updated_at']);

/**
 * Operators whose value is a list. Includes both react-querybuilder spellings
 * (`notIn`) and the backend's current spellings (`not_in`, `contains_*`) so the
 * editors behave correctly before and after the backend is aligned to RQB.
 */
export const MULTI_VALUE_OPERATORS = new Set([
  'in',
  'notIn',
  'containsAll',
  'containsAny',
  'notContainsAny',
]);

/** Operators whose value is a pair `[from, to]`. */
export const RANGE_OPERATORS = new Set(['between', 'notBetween']);

/** Operators that take no value. */
export const NO_VALUE_OPERATORS = new Set(['null', 'notNull']);

const RQB_OP_BY_NAME: Record<string, Operator> = Object.fromEntries(
  defaultOperators.map((op) => [op.name, op])
);

/**
 * Resolve operator definitions from react-querybuilder by name, falling back to
 * the backend-provided `{ name, label }` when the name is not a known RQB
 * operator (e.g. `contains_all`). Output operator names are written verbatim to
 * the payload, matching the backend `QueryBuilderOperator` enum.
 */
const resolveOperators = (field: RQBFieldOut): Operator[] =>
  field.operators.map(
    (op) => RQB_OP_BY_NAME[op.name] ?? { name: op.name, label: op.label }
  );

/** Map a single backend filter field to a react-querybuilder `Field`. */
export const mapField = (field: RQBFieldOut): Field => {
  const base: Field = {
    name: field.name,
    value: field.name,
    label: field.label,
    operators: resolveOperators(field),
  };

  // Async + date fields use custom value editors; editor type is irrelevant.
  if (ASYNC_FIELD_RESOURCE_MAP[field.name] || DATE_FIELDS.has(field.name)) {
    return base;
  }

  switch (field.value_editor_type) {
    case 'number':
      return { ...base, inputType: 'number' };
    case 'checkbox':
      return { ...base, valueEditorType: 'checkbox', defaultValue: false };
    case 'select':
      return {
        ...base,
        valueEditorType: 'select',
        values: field.values.map((v) => ({ name: v.name, label: v.label })),
      };
    case 'text':
    default:
      return base;
  }
};
